import * as v from 'valibot';
import {
  buildSortPlansPayloads,
  FIRST_LOAN_TIME_VALUES,
  LAST_LOAN_TIME_VALUES,
  type Sf0726FormModel,
  type Sf0726LoanInput,
} from 'tio-common/utils/strategy-finder-0726';

export {
  loanInputsFromSummary,
  withTestLoanCards,
  answerProfilePrefill,
  wellnessNsldsPrefill,
  CURRENT_PLAN_OPTION_KEYS,
} from 'tio-common/utils/strategy-finder-0726';
export type { Sf0726LoanInput, Sf0726NsldsPrefill } from 'tio-common/utils/strategy-finder-0726';

export const REQUESTING_APP = 'IDR-0726';

// The plan set is filtered on the backend (tio-api SortPlansEstimatorFilter) for
// IDR-0726 before it is persisted, so the stored calculation_output already
// contains only the current plan + income-driven plans (Direct/FFEL; PLUS is left
// whole). Components parse it via parseResultSections, which still applies its
// standard cleanup (e.g. suggestedPlans) — only the IDR-specific plan-key
// filtering that used to live here has moved server-side.

// The fields the user actually fills in — all hardcoded assumptions are excluded.
export type Idr0726FormModel = {
  agi: number; // cents
  numberOfAdults: number;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  madePayments: number; // qualifying payments made
  affordPayments: string; // 'yes' | 'no' — can they afford their current payments
};

export const idr0726FormSchema = v.object({
  agi: v.pipe(
    v.number('Income must be greater than $0'),
    v.minValue(1, 'Income must be greater than $0')
  ),
  numberOfAdults: v.pipe(
    v.number('Please select a response'),
    v.picklist([1, 2], 'Please select a response')
  ),
  numberOfChildren: v.pipe(v.number('Must be 0 or greater'), v.minValue(0, 'Must be 0 or greater')),
  firstLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...FIRST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  lastLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...LAST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  madePayments: v.pipe(v.number('Please enter a number'), v.minValue(0, 'Must be 0 or greater')),
  affordPayments: v.pipe(
    v.string('Please select a response'),
    v.picklist(['yes', 'no'], 'Please select a response')
  ),
});

// Builds the full SF-0726 payload, filling in all hardcoded assumptions so they
// are never surfaced to the user but still reach the backend correctly. The
// borrower is assumed to want lower monthly payments and be pursuing
// income-driven forgiveness as a private-sector employee.
export function buildIdr0726Payloads(model: Idr0726FormModel, loanInputs: Sf0726LoanInput[]) {
  const fullModel: Sf0726FormModel = {
    payoffGoal: 'slow',
    affordPayments: model.affordPayments,
    agi: model.agi,
    numberOfAdults: model.numberOfAdults,
    numberOfChildren: model.numberOfChildren,
    firstLoanTime: model.firstLoanTime,
    lastLoanTime: model.lastLoanTime,
    residenceState: 'CA',
    currentlyWorkingToward: 'yes',
    currentForgivenessProgram: 'IncomeDrivenLoanForgiveness',
    madePayments: model.madePayments,
    isGov: false,
    isTeacher: false,
    isOther: true,
    planInGov: '',
    planAsTeacher: '',
    employmentMonths: 0,
    tlfQualifies17500: false,
  };
  return buildSortPlansPayloads(fullModel, loanInputs);
}

// Only persists the fields the user actually entered. Hardcoded assumptions
// (payoffGoal, affordPayments, residenceState, employment, forgiveness program,
// etc.) are intentionally omitted so the user's real answers in other tools
// are not overwritten.
export function idr0726AnswerProfileUpdates(model: Idr0726FormModel) {
  return {
    householdIncome: model.agi,
    numberOfAdults: model.numberOfAdults,
    numberOfChildren: model.numberOfChildren,
    householdSize: model.numberOfAdults + model.numberOfChildren,
    federalFirstLoanTime: model.firstLoanTime,
    federalLastLoanTime: model.lastLoanTime,
    madePayments: model.madePayments,
    affordPayments: model.affordPayments,
  };
}
